<div class="flex flex-col gap-6 h-full">
  @switch (activeTab) {

    <!-- ===== ALLGEMEIN TAB ===== -->
    @case ('general') {
      <span class="text-xs font-medium text-gray-500">Enthält allgemeine Metadaten zur Spezifikation.</span>

      <!-- Row 1: Titel + Beschreibung -->
      <div class="flex flex-row gap-4 items-end">
        <div class="flex-1">
          <app-change-marker (acceptClicked)="fieldAccepted.emit('name')" (rejectClicked)="fieldRejected.emit('name')"
                             [change]="$safeNavigationMigration(fieldChanges?.get('name'))"
                             [reviewKey]="reviewKeyPrefix + ':name'" label="Titel">
            <bic-input [(ngModel)]="specification.name"
                       (ngModelChange)="emitChange()"
                       [errorCondition]="submitted && !specification.name?.trim()"
                       [label]="'Titel'"
                       [placeholder]="'Titel eingeben*'"
                       [required]="true"
                       [showX]="false">
            </bic-input>
          </app-change-marker>
        </div>
        <div class="flex-1">
          <app-change-marker (acceptClicked)="fieldAccepted.emit('description')" (rejectClicked)="fieldRejected.emit('description')"
                             [change]="$safeNavigationMigration(fieldChanges?.get('description'))"
                             [reviewKey]="reviewKeyPrefix + ':description'" label="Beschreibung">
            <bic-input [(ngModel)]="specification.description"
                       (ngModelChange)="emitChange()"
                       [label]="'Beschreibung'"
                       [placeholder]="'Beschreibung eingeben'"
                       [showX]="false">
            </bic-input>
          </app-change-marker>
        </div>
      </div>

      <!-- Row 2: Anwendbarkeit (Dropdown) + Anweisung -->
      <div class="flex flex-row gap-4 items-end">
        <div class="flex-1">
          <app-change-marker (acceptClicked)="fieldAccepted.emit('necessity')" (rejectClicked)="fieldRejected.emit('necessity')"
                             [change]="$safeNavigationMigration(fieldChanges?.get('necessity'))"
                             [reviewKey]="reviewKeyPrefix + ':necessity'" label="Anwendbarkeit">
            <bic-select [(ngModel)]="specification.necessity"
                        (ngModelChange)="emitChange()"
                        [label]="'Anwendbarkeit'"
                        [placeholder]="'Auswählen'">
              @for (opt of NECESSITY_OPTIONS; track opt.value) {
                <bic-dropdown-element [value]="opt.value">{{ opt.label }}</bic-dropdown-element>
              }
            </bic-select>
          </app-change-marker>
        </div>
        <div class="flex-1">
          <app-change-marker (acceptClicked)="fieldAccepted.emit('instruction')" (rejectClicked)="fieldRejected.emit('instruction')"
                             [change]="$safeNavigationMigration(fieldChanges?.get('instruction'))"
                             [reviewKey]="reviewKeyPrefix + ':instruction'" label="Anweisung">
            <bic-input [(ngModel)]="specification.instruction"
                       (ngModelChange)="emitChange()"
                       [label]="'Anweisung'"
                       [placeholder]="'Anweisung eingeben'"
                       [showX]="false">
            </bic-input>
          </app-change-marker>
        </div>
      </div>

      <!-- Row 3: IFC-Versionen (Checkboxen) + Identifikator -->
      <div class="flex flex-row gap-4">
        <div class="flex-1">
          <!-- Der Titel steht im Marker, damit er im Kopfzeilen-Modus (data-proposal-label) dem Label der
               Kopfzeile weicht. -->
          <app-change-marker (acceptClicked)="fieldAccepted.emit('ifcVersions')" (rejectClicked)="fieldRejected.emit('ifcVersions')"
                             [change]="$safeNavigationMigration(fieldChanges?.get('ifcVersions'))"
                             [reviewKey]="reviewKeyPrefix + ':ifcVersions'" label="IFC-Versionen">
            <div class="flex flex-col gap-4">
              <span class="text-sm font-medium text-gray-900" data-proposal-label>IFC-Versionen</span>
              <div class="flex flex-row gap-4" data-proposal-group>
                @for (version of IFC_VERSIONS; track version) {
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox"
                           [checked]="specification.ifcVersions.includes(version)"
                           (change)="toggleIfcVersion(version)"
                           class="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500">
                    <span class="text-sm font-medium text-gray-900">{{ version }}</span>
                  </label>
                }
              </div>
            </div>
          </app-change-marker>
        </div>
        <div class="flex-1">
          <app-change-marker (acceptClicked)="fieldAccepted.emit('identifier')" (rejectClicked)="fieldRejected.emit('identifier')"
                             [change]="$safeNavigationMigration(fieldChanges?.get('identifier'))"
                             [reviewKey]="reviewKeyPrefix + ':identifier'" label="Identifikator">
            <bic-input [(ngModel)]="specification.identifier"
                       (ngModelChange)="emitChange()"
                       [label]="'Identifikator'"
                       [placeholder]="'Identifikator eingeben'"
                       [showX]="false">
            </bic-input>
          </app-change-marker>
        </div>
      </div>

      <!-- Trennlinie -->
      <hr class="border-gray-200">

      <!-- Zusammenfassung -->
      <span class="text-xs text-gray-900 leading-relaxed">
        @if (specification.applicability.length > 0 || specification.requirements.length > 0) {
          @if (specification.applicability.length > 0) {
            @if (specification.necessity === 'optional') { Das Modell kann Elemente mit }
            @else if (specification.necessity === 'prohibited') { Das Modell darf keine Elemente mit }
            @else { Das Modell muss Elemente mit }
            @for (facet of specification.applicability; track facet.id; let last = $last) {
              @switch (facet.type) {
                @case ('entity') { der Entität {{ vfd(facet.entityName) }} }
                @case ('entityGroup') { der Entitätengruppe {{ entityGroupLabel(facet) }} }
                @case ('attribute') { Attribut {{ vfd(facet.attributeName) }} }
                @case ('property') { der Eigenschaft {{ vfd(facet.propertyName) }} }
                @case ('classification') { der Klassifikation {{ vfd(facet.classificationSystem) }} }
                @case ('material') { dem Material {{ vfd(facet.materialName) }} }
                @case ('partOf') { Teil von {{ vfd(facet.partOfEntityName) }} }
              }
              @if (!last) { und }
            }
            beinhalten.
          }
          @if (specification.requirements.length > 0) {
            <br>{{ requirementsSummary }}
          }
        }
      </span>
    }

    <!-- ===== ANWENDBARKEIT TAB ===== -->
    @case ('applicability') {
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-4">
          <bic-button (onClick)="addApplicabilityFacet()" buttonType="white">
            <fa-icon [icon]="faPlus" class="text-sm" iconLeft></fa-icon>
            Neue Anwendbarkeit
          </bic-button>
          @if (specification.applicability.length > 0) {
            <bic-button (onClick)="toggleAllApplicability()" buttonType="white">
              <fa-icon [icon]="allApplicabilityExpanded ? faAnglesUp : faAnglesDown" class="text-sm" iconLeft></fa-icon>
              {{ allApplicabilityExpanded ? 'Alle einklappen' : 'Alle ausklappen' }}
            </bic-button>
          }
        </div>

        <span class="text-xs font-medium text-gray-500">Definiert, auf welche Objekte diese Spezifikation angewendet wird.</span>

        <app-change-marker (acceptClicked)="fieldAccepted.emit('applicability')" (rejectClicked)="fieldRejected.emit('applicability')"
                           [block]="true" [change]="$safeNavigationMigration(fieldChanges?.get('applicability'))">
          <div class="flex flex-col gap-4">
            @for (facet of specification.applicability; track facet.id; let i = $index) {
              <app-ids-facet
                [facet]="facet"
                [ifcVersions]="specification.ifcVersions"
                [submitted]="submitted"
                [changes]="facetChanges('applicability', i)"
                (changeAccepted)="onFacetChangeAccepted('applicability', i, $event)"
                (changeRejected)="onFacetChangeRejected('applicability', i, $event)"
                (facetAccepted)="facetAccepted.emit({block: 'applicability', index: i})"
                (facetRejected)="facetRejected.emit({block: 'applicability', index: i})"
                (facetChange)="onApplicabilityFacetChange(i, $event)"
                (remove)="removeApplicabilityFacet(i)"
                (duplicate)="duplicateApplicabilityFacet(i)">
              </app-ids-facet>
            }
          </div>
        </app-change-marker>

        <!-- Vom Vorschlag entfernte Facetten: stehen nicht mehr in der Liste, ✓ bestätigt, ✗ stellt wieder her. -->
        @for (removed of removedFacets; track removed) {
          @if (removed.block === 'applicability') {
            <app-removed-rule-card (removeClicked)="removedFacetAccepted.emit(removed)"
                                   (restoreClicked)="removedFacetRejected.emit(removed)"
                                   [itemName]="removedFacetLabel(removed.facet)"
                                   itemLabel="Anwendbarkeit">
            </app-removed-rule-card>
          }
        }

        @if (specification.applicability.length === 0) {
          <div class="text-sm text-gray-500 italic p-4 border border-gray-200 rounded-lg text-center">
            Keine Facetten definiert. Fügen Sie eine Anwendbarkeit hinzu.
          </div>
        }

        <!-- Trennlinie + Zusammenfassung -->
        <hr class="border-gray-200">
        <span class="text-xs text-gray-900 leading-relaxed">
          @if (specification.applicability.length > 0) {
            @if (specification.necessity === 'optional') { Das Modell kann Elemente mit }
            @else if (specification.necessity === 'prohibited') { Das Modell darf keine Elemente mit }
            @else { Das Modell muss Elemente mit }
            @for (facet of specification.applicability; track facet.id; let last = $last) {
              @switch (facet.type) {
                @case ('entity') { der Entität {{ vfd(facet.entityName) }} }
                @case ('entityGroup') { der Entitätengruppe {{ entityGroupLabel(facet) }} }
                @case ('attribute') { Attribut {{ vfd(facet.attributeName) }} }
                @case ('property') { der Eigenschaft {{ vfd(facet.propertyName) }} }
                @case ('classification') { der Klassifikation {{ vfd(facet.classificationSystem) }} }
                @case ('material') { dem Material {{ vfd(facet.materialName) }} }
                @case ('partOf') { Teil von {{ vfd(facet.partOfEntityName) }} }
              }
              @if (!last) { und }
            }
            beinhalten.
          }
        </span>
      </div>
    }

    <!-- ===== ANFORDERUNGEN TAB ===== -->
    @case ('requirements') {
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-4">
          <bic-button (onClick)="addRequirementFacet()" buttonType="white">
            <fa-icon [icon]="faPlus" class="text-sm" iconLeft></fa-icon>
            Neue Anforderung
          </bic-button>
          @if (specification.requirements.length > 0) {
            <bic-button (onClick)="toggleAllRequirements()" buttonType="white">
              <fa-icon [icon]="allRequirementsExpanded ? faAnglesUp : faAnglesDown" class="text-sm" iconLeft></fa-icon>
              {{ allRequirementsExpanded ? 'Alle einklappen' : 'Alle ausklappen' }}
            </bic-button>
          }
        </div>

        <span class="text-xs font-medium text-gray-500">Definiert, welche Anforderungen erfüllt werden müssen.</span>

        <app-change-marker (acceptClicked)="fieldAccepted.emit('requirements')" (rejectClicked)="fieldRejected.emit('requirements')"
                           [block]="true" [change]="$safeNavigationMigration(fieldChanges?.get('requirements'))">
          <div class="flex flex-col gap-4">
            @for (facet of specification.requirements; track facet.id; let i = $index) {
              <app-ids-facet
                [facet]="facet"
                [ifcVersions]="specification.ifcVersions"
                [isRequirement]="true"
                [submitted]="submitted"
                [changes]="facetChanges('requirements', i)"
                (changeAccepted)="onFacetChangeAccepted('requirements', i, $event)"
                (changeRejected)="onFacetChangeRejected('requirements', i, $event)"
                (facetAccepted)="facetAccepted.emit({block: 'requirements', index: i})"
                (facetRejected)="facetRejected.emit({block: 'requirements', index: i})"
                (facetChange)="onRequirementFacetChange(i, $event)"
                (remove)="removeRequirementFacet(i)"
                (duplicate)="duplicateRequirementFacet(i)">
              </app-ids-facet>
            }
          </div>
        </app-change-marker>

        <!-- Vom Vorschlag entfernte Facetten: stehen nicht mehr in der Liste, ✓ bestätigt, ✗ stellt wieder her. -->
        @for (removed of removedFacets; track removed) {
          @if (removed.block === 'requirements') {
            <app-removed-rule-card (removeClicked)="removedFacetAccepted.emit(removed)"
                                   (restoreClicked)="removedFacetRejected.emit(removed)"
                                   [itemName]="removedFacetLabel(removed.facet)"
                                   itemLabel="Anforderung">
            </app-removed-rule-card>
          }
        }

        @if (specification.requirements.length === 0) {
          <div class="text-sm text-gray-500 italic p-4 border border-gray-200 rounded-lg text-center">
            Keine Anforderungen definiert. Fügen Sie eine Anforderung hinzu.
          </div>
        }

        <!-- Trennlinie + Zusammenfassung -->
        <hr class="border-gray-200">
        <span class="text-xs text-gray-900 leading-relaxed">
          @if (specification.requirements.length > 0) {
            {{ requirementsSummary }}
          }
        </span>
      </div>
    }
  }
</div>
